import React, { useState, useEffect, useRef } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import HostTriviaAdmin from './HostTriviaAdmin';
import { useGameSocket } from '../hooks/useGameSocket';
import { BuzzerState } from './BuzzerStatus';
import HostTutorial from './HostTutorial';
import InviteOverlay from './InviteOverlay';
import gamy from '../games/gamy.json';
import './Host.css';

const FALLBACK_GAME = (gamy as any).game;

interface TeamState {
  name: string;
  score: number;
}

interface ActiveClue {
  category: string;
  value: number;
  clue: string;
  answer: string;
  isDailyDouble: boolean;
}

interface ClueMeta {
  roundName: string;
  categoryIndex: number;
  clueIndex: number;
  forced: boolean;
}

const BUZZ_SECONDS = 5;

interface ServerState {
  teams: TeamState[];
  activeClue: ActiveClue | null;
  activeClueMeta: ClueMeta | null;
  round: string;
  buzzer: BuzzerState;
  chosenClues: Array<{ round: string; categoryIndex: number; clueIndex: number }>;
  gameStarted: boolean;
  buzzerCount: number[];
  rosters: string[][];
  captains: (string | null)[];
  ddWager: { teamIndex: number; amount: number | null } | null;
  fjPhase: string | null;
  fjWagers: (number | null)[];
  fjAnswers: (string | null)[];
  fjReviewIndex: number;
  fjReviewShowWager: boolean;
  fjReviewResults: (string | null)[];
  invitePanel: { open: boolean; teamIndex: number };
  triviaMode: {
    currentQuestion: TriviaQuestion | null;
    showAnswer: boolean;
    controllerCount: number;
  };
  triviaQuestions: TriviaQuestion[];
  gameData: any | null;
}

interface TriviaQuestion {
  id: string;
  question: string;
  answer: string;
}

const ROUND_LABELS: Record<string, string> = {
  single: 'Jeopardy Round',
  double: 'Double Jeopardy',
  final: 'Final Jeopardy',
  done: 'Game Over',
};

const TEAM_COLORS = ['#1565c0', '#b71c1c', '#2e7d32'];

function formatScore(score: number): string {
  if (score < 0) return `-$${Math.abs(score).toLocaleString()}`;
  return `$${score.toLocaleString()}`;
}

export default function Host() {
  const [serverState, setServerState] = useState<ServerState | null>(null);
  const [amounts, setAmounts] = useState<number[]>([200, 200, 200]);
  const [editingTeam, setEditingTeam] = useState<number | null>(null);
  const [editValue, setEditValue] = useState('');
  const [resetConfirm, setResetConfirm] = useState(false);
  const [boardViewRound, setBoardViewRound] = useState<'single' | 'double' | 'final'>('single');
  const [showTutorial, setShowTutorial] = useState(false);
  // Daily Double host-override wager input (used when captain's phone is dead)
  const [ddWagerInput, setDdWagerInput] = useState('');
  // Buzz countdown (visual only — host still drives Correct/Wrong)
  const [buzzNow, setBuzzNow] = useState(Date.now());
  const buzzTickRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // DD timer on host
  const [ddTimeLeft, setDdTimeLeft] = useState<number | null>(null);
  const ddTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const prevDdShown = useRef(false);
  // Final Jeopardy wager overrides (host can edit per-team)
  const [fjWagerOverrides, setFjWagerOverrides] = useState<string[]>(['', '', '']);
  // Invite Players panel is server-driven so it mirrors on the game board too.

  const { send, connected } = useGameSocket((msg) => {
    if (msg.type === 'STATE') {
      setServerState({
        teams: msg.teams,
        activeClue: msg.activeClue,
        activeClueMeta: msg.activeClueMeta ?? null,
        round: msg.round,
        buzzer: msg.buzzer ?? { armed: false, activeBuzz: null, wrongTeams: [], answered: false },
        chosenClues: msg.chosenClues ?? [],
        gameStarted: !!msg.gameStarted,
        buzzerCount: msg.buzzerCount ?? [0, 0, 0],
        rosters: msg.rosters ?? [[], [], []],
        captains: msg.captains ?? [null, null, null],
        ddWager: msg.ddWager ?? null,
        fjPhase: msg.fjPhase ?? null,
        fjWagers: msg.fjWagers ?? [null, null, null],
        fjAnswers: msg.fjAnswers ?? [null, null, null],
        fjReviewIndex: msg.fjReviewIndex ?? 0,
        fjReviewShowWager: !!msg.fjReviewShowWager,
        fjReviewResults: msg.fjReviewResults ?? [null, null, null],
        invitePanel: msg.invitePanel ?? { open: false, teamIndex: 0 },
        triviaMode: msg.triviaMode ?? { currentQuestion: null, showAnswer: false, controllerCount: 0 },
        triviaQuestions: Array.isArray(msg.triviaQuestions) ? msg.triviaQuestions : [],
        gameData: msg.gameData ?? null,
      });
    }
  });

  // When active clue changes, pre-fill amount inputs and reset DD form
  useEffect(() => {
    const val = serverState?.activeClue?.value;
    if (val) setAmounts([val, val, val]);
    setDdWagerInput('');
  }, [serverState?.activeClue?.clue]);

  // DD countdown timer — starts when ddShown becomes true
  const ddShown = !!serverState?.buzzer?.ddShown;
  useEffect(() => {
    if (ddShown && !prevDdShown.current) {
      setDdTimeLeft(5);
      if (ddTimerRef.current) clearInterval(ddTimerRef.current);
      ddTimerRef.current = setInterval(() => {
        setDdTimeLeft(t => {
          if (t === null || t <= 1) { clearInterval(ddTimerRef.current!); return 0; }
          return t - 1;
        });
      }, 1000);
    }
    if (!ddShown) {
      if (ddTimerRef.current) clearInterval(ddTimerRef.current);
      setDdTimeLeft(null);
    }
    prevDdShown.current = ddShown;
    return () => { if (ddTimerRef.current) clearInterval(ddTimerRef.current); };
  }, [ddShown]); // eslint-disable-line

  function adjust(teamIndex: number, sign: 1 | -1) {
    send({ type: 'SCORE_ADJUST', teamIndex, delta: sign * (amounts[teamIndex] || 0) });
  }

  function startEdit(i: number) {
    setEditingTeam(i);
    setEditValue(String(serverState?.teams[i]?.score ?? 0));
  }

  function confirmEdit() {
    if (editingTeam !== null) {
      send({ type: 'SCORE_SET', teamIndex: editingTeam, score: editValue });
      setEditingTeam(null);
    }
  }

  function handleEditKey(e: React.KeyboardEvent) {
    if (e.key === 'Enter') confirmEdit();
    if (e.key === 'Escape') setEditingTeam(null);
  }

  function resetScores() {
    if (!resetConfirm) {
      setResetConfirm(true);
      setTimeout(() => setResetConfirm(false), 3000);
      return;
    }
    send({ type: 'RESET_SCORES' });
    setResetConfirm(false);
  }

  function newGame() {
    if (!resetConfirm) {
      setResetConfirm(true);
      setTimeout(() => setResetConfirm(false), 3000);
      return;
    }
    send({ type: 'RESET_GAME' });
    setResetConfirm(false);
  }

  function jumpToRound(r: string) {
    send({ type: 'SET_ROUND', round: r });
  }

  function forceClue(roundName: string, categoryIndex: number, clueIndex: number) {
    const currentGame = (serverState?.gameData?.game) ?? FALLBACK_GAME;
    const board = (currentGame as any)[roundName];
    if (!board) return;
    const category = board[categoryIndex];
    const clue = category?.clues?.[clueIndex];
    if (!clue) return;
    send({
      type: 'FORCE_CLUE',
      roundName,
      categoryIndex,
      clueIndex,
      category: category.category,
      value: clue.value,
      clue: clue.clue,
      answer: clue.solution,
      isDailyDouble: !!clue.dailyDouble,
    });
  }

  function forceFinalClue() {
    const final = (serverState?.gameData?.game?.final) ?? FALLBACK_GAME.final;
    if (!final) return;
    send({
      type: 'FORCE_CLUE',
      roundName: 'final',
      categoryIndex: 0,
      clueIndex: 0,
      category: final.category,
      value: 0,
      clue: final.clue,
      answer: final.solution,
      isDailyDouble: false,
    });
  }

  function revealAnswer() {
    send({ type: 'REVEAL_ANSWER' });
  }

  function buzzResult(correct: boolean) {
    send({ type: 'BUZZ_RESULT', correct });
  }

  function armBuzzers() {
    send({ type: 'BUZZ_ARM' });
  }

  function dismissClue() {
    send({ type: 'CLUE_DISMISSED' });
  }

  function ddPickTeam(teamIndex: number) {
    send({ type: 'DD_SET_TEAM', teamIndex });
  }

  function ddOverrideWager() {
    const amount = parseInt(ddWagerInput.replace(/\D/g, ''), 10);
    if (isNaN(amount) || amount < 0) return;
    send({ type: 'DD_WAGER', amount });
    setDdWagerInput('');
  }

  function setCaptain(teamIndex: number, playerName: string) {
    send({ type: 'SET_CAPTAIN', teamIndex, playerName });
  }

  function ddResult(correct: boolean) {
    send({ type: 'DD_RESULT', correct });
  }

  function buzzDisarm() {
    send({ type: 'BUZZ_DISARM' });
  }

  function fjStartWagering() {
    setFjWagerOverrides(['', '', '']);
    send({ type: 'FJ_START_WAGERING' });
  }

  function fjSetWager(teamIndex: number, value: string) {
    const overrides = [...fjWagerOverrides];
    overrides[teamIndex] = value;
    setFjWagerOverrides(overrides);
    const amount = parseInt(value.replace(/\D/g, ''), 10);
    if (!isNaN(amount)) {
      send({ type: 'FJ_WAGER', teamIndex, amount });
    }
  }

  function fjRevealQuestion() {
    send({ type: 'FJ_REVEAL_QUESTION' });
  }

  function fjStartTimer() {
    send({ type: 'FJ_START_TIMER' });
  }

  function fjStartReview() {
    send({ type: 'FJ_START_REVIEW' });
  }

  function fjReviewJudge(correct: boolean) {
    send({ type: 'FJ_REVIEW_JUDGE', correct });
  }

  function fjReviewNext() {
    send({ type: 'FJ_REVIEW_NEXT' });
  }

  function triviaAdd(question: string, answer: string, explainer: string) {
    send({ type: 'TRIVIA_ADD', question, answer, explainer });
  }

  function triviaEdit(id: string, question: string, answer: string, explainer: string) {
    send({ type: 'TRIVIA_EDIT', id, question, answer, explainer });
  }

  function triviaDelete(id: string) {
    send({ type: 'TRIVIA_DELETE', id });
  }


  function testBuzz(teamIndex: number) {
    send({ type: 'TEST_BUZZ', teamIndex });
  }

  // Tick the buzz countdown 10×/s while a team is buzzed in.
  const activeBuzzAt = serverState?.buzzer?.activeBuzz?.at ?? null;
  useEffect(() => {
    if (activeBuzzAt !== null) {
      buzzTickRef.current = setInterval(() => setBuzzNow(Date.now()), 100);
    } else if (buzzTickRef.current) {
      clearInterval(buzzTickRef.current);
    }
    return () => { if (buzzTickRef.current) clearInterval(buzzTickRef.current); };
  }, [activeBuzzAt]);

  const origin = window.location.origin;
  const { activeClue, teams, round, buzzer } = serverState ?? {
    activeClue: null,
    teams: [],
    round: 'single',
    buzzer: { armed: false, activeBuzz: null, wrongTeams: [], answered: false },
  };

  const activeBuzz = buzzer?.activeBuzz ?? null;
  const activeBuzzTeamName = activeBuzz !== null
    ? (teams[activeBuzz.teamIndex]?.name ?? `Team ${activeBuzz.teamIndex + 1}`)
    : null;
  const activeBuzzLabel = activeBuzz !== null
    ? (activeBuzz.playerName
        ? `${activeBuzz.playerName} (${activeBuzzTeamName}) BUZZED IN`
        : `${activeBuzzTeamName} BUZZED IN`)
    : null;
  const activeBuzzColor = activeBuzz !== null
    ? (TEAM_COLORS[activeBuzz.teamIndex] ?? '#1565c0')
    : null;

  return (
    <div className="host-panel">

      {/* Header */}
      <div className="host-header">
        <div className="host-title">GAMY JEOPARDY — HOST</div>
        <div className="host-meta">
          <span className={`host-conn ${connected ? 'connected' : 'disconnected'}`}>
            {connected ? '● Connected' : '○ Reconnecting…'}
          </span>
          <span className="host-round">{ROUND_LABELS[round] ?? round}</span>
          <button
            className="host-invite-btn"
            onClick={() => send({ type: 'INVITE_OPEN', teamIndex: 0 })}
            title="Show big QR codes (mirrored on game board)"
          >
            📱 Invite Players
          </button>
          <button className="host-help-btn" onClick={() => setShowTutorial(true)} title="How to host">
            ? Help
          </button>
        </div>
      </div>

      {showTutorial && <HostTutorial onClose={() => setShowTutorial(false)} />}

      {serverState?.invitePanel?.open && (
        <InviteOverlay
          team={serverState.invitePanel.teamIndex}
          teams={teams}
          rosters={serverState.rosters ?? [[], [], []]}
          captains={serverState.captains ?? [null, null, null]}
          origin={origin}
          interactive
          onClose={() => send({ type: 'INVITE_CLOSE' })}
          onPrev={() => {
            const ti = serverState.invitePanel.teamIndex;
            const total = teams.length || 3;
            send({ type: 'INVITE_SET_TEAM', teamIndex: (ti - 1 + total) % total });
          }}
          onNext={() => {
            const ti = serverState.invitePanel.teamIndex;
            const total = teams.length || 3;
            send({ type: 'INVITE_SET_TEAM', teamIndex: (ti + 1) % total });
          }}
          onJumpTo={(i) => send({ type: 'INVITE_SET_TEAM', teamIndex: i })}
          onMakeCaptain={(name) => setCaptain(serverState.invitePanel.teamIndex, name)}
        />
      )}

      {/* Active Clue */}
      <section className="host-section">
        <div className="host-section-label">ACTIVE CLUE</div>
        {activeClue ? (
          <div className="host-clue-card">
            <div className="host-clue-meta">
              {activeClue.isDailyDouble && (
                <span className="host-dd-badge">★ DAILY DOUBLE</span>
              )}
              <span className="host-clue-cat">{activeClue.category}</span>
              <span className="host-clue-value">${activeClue.value.toLocaleString()}</span>
            </div>
            <div className="host-clue-text">{activeClue.clue}</div>
            <div className="host-answer-label">ANSWER</div>
            <div className="host-answer-text">{activeClue.answer}</div>

            {!activeClue.isDailyDouble && !buzzer?.answered && (
              <button className="host-reveal-btn" onClick={revealAnswer}>
                👁 Reveal Answer on Screen
              </button>
            )}
            {buzzer?.answered && (
              <div className="host-bz-answered">✅ Answer revealed on game screen</div>
            )}

            {/* ── Daily Double: Step 1 — pick the team that found it ── */}
            {activeClue.isDailyDouble && !serverState?.ddWager && !buzzer?.answered && (
              <div className="host-dd-form">
                <div className="host-dd-form-title">Who found it?</div>
                <div className="host-dd-team-btns">
                  {teams.map((team, i) => (
                    <button
                      key={i}
                      className="host-dd-team-btn"
                      style={{ borderColor: TEAM_COLORS[i], color: TEAM_COLORS[i] }}
                      onClick={() => ddPickTeam(i)}
                    >
                      {team.name}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* ── Daily Double: Step 2 — waiting for captain wager (with override) ── */}
            {activeClue.isDailyDouble && serverState?.ddWager && serverState.ddWager.amount === null && !buzzer?.answered && (
              <div className="host-dd-form">
                <div className="host-dd-form-title">
                  {(() => {
                    const ti = serverState.ddWager.teamIndex;
                    const cap = serverState.captains?.[ti] ?? null;
                    const teamName = teams[ti]?.name ?? `Team ${ti + 1}`;
                    return cap
                      ? `Waiting for ${cap} (${teamName} captain) to wager…`
                      : `${teamName}: no captain on a phone — enter wager below`;
                  })()}
                </div>
                <div className="host-dd-wager-row">
                  <label className="host-dd-wager-label">Override $</label>
                  <input
                    className="host-dd-wager-input"
                    type="number"
                    min="0"
                    value={ddWagerInput}
                    onChange={e => setDdWagerInput(e.target.value)}
                    placeholder="Type & submit to override"
                  />
                  <button
                    className="host-dd-show-btn"
                    disabled={!ddWagerInput.trim()}
                    onClick={ddOverrideWager}
                    style={{ marginLeft: 8 }}
                  >
                    ▶ Submit Override
                  </button>
                </div>
              </div>
            )}

            {activeClue.isDailyDouble && buzzer?.ddShown && !buzzer?.answered && serverState?.ddWager && serverState.ddWager.amount !== null && (() => {
              const ddTi = serverState.ddWager.teamIndex;
              const ddAmount: number = serverState.ddWager.amount;
              return (
                <div className="host-dd-judge">
                  <div className="host-dd-judge-info">
                    <span className="host-dd-judge-team" style={{ color: TEAM_COLORS[ddTi] }}>
                      {teams[ddTi]?.name}
                    </span>
                    <span className="host-dd-judge-wager">
                      wagered ${ddAmount.toLocaleString()}
                    </span>
                    {ddTimeLeft !== null && (
                      <span className={`host-dd-timer ${ddTimeLeft <= 3 ? 'host-dd-timer-urgent' : ''}`}>
                        {ddTimeLeft}s
                      </span>
                    )}
                  </div>
                  <div className="host-bz-btns">
                    <button className="host-bz-btn host-bz-correct" onClick={() => ddResult(true)}>
                      ✓ Correct +${ddAmount.toLocaleString()}
                    </button>
                    <button className="host-bz-btn host-bz-wrong" onClick={() => ddResult(false)}>
                      ✗ Wrong −${ddAmount.toLocaleString()}
                    </button>
                  </div>
                  <button className="host-reveal-btn" onClick={revealAnswer} style={{ marginTop: 8 }}>
                    👁 Reveal Answer on Screen
                  </button>
                </div>
              );
            })()}

            {/* Buzzer status within clue card */}
            {buzzer && !activeClue.isDailyDouble && (
              <div className="host-buzzer-status">
                {/* Idle (clue shown, not yet armed) — show Arm button */}
                {!buzzer.armed && !activeBuzz && !buzzer.answered && (
                  <button className="host-arm-btn" onClick={armBuzzers}>
                    🔔 Arm Buzzers
                  </button>
                )}
                {buzzer.armed && !activeBuzz && (
                  <div className="host-bz-armed">
                    🔔 Buzzers Armed
                    <button className="host-bz-disarm-btn" onClick={buzzDisarm}>Disarm</button>
                  </div>
                )}
                {activeBuzz !== null && (() => {
                  const elapsedMs = buzzNow - activeBuzz.at;
                  const timeLeft = Math.max(0, BUZZ_SECONDS - elapsedMs / 1000);
                  const expired = timeLeft <= 0;
                  return (
                    <div className="host-bz-active" style={{ '--bz-color': activeBuzzColor } as any}>
                      <span className="host-bz-team">{activeBuzzLabel}</span>
                      <span className={`host-bz-timer ${expired ? 'host-bz-timer-expired' : timeLeft < 2.5 ? 'host-bz-timer-urgent' : ''}`}>
                        {expired ? "TIME'S UP" : `${timeLeft.toFixed(1)}s`}
                      </span>
                      <div className="host-bz-btns">
                        <button className="host-bz-btn host-bz-correct" onClick={() => buzzResult(true)}>
                          ✓ Correct
                        </button>
                        <button className="host-bz-btn host-bz-wrong" onClick={() => buzzResult(false)}>
                          ✗ Wrong
                        </button>
                      </div>
                    </div>
                  );
                })()}
                {buzzer.wrongTeams.length > 0 && !activeBuzz && !buzzer.answered && (
                  <div className="host-bz-locked">
                    Locked out: {buzzer.wrongTeams.map(i => teams[i]?.name ?? `Team ${i + 1}`).join(', ')}
                    {' · '}
                    <button className="host-bz-disarm-btn" onClick={buzzDisarm}>Disarm all</button>
                  </div>
                )}
              </div>
            )}
          </div>
        ) : (
          <div className="host-no-clue">No active clue</div>
        )}
        {activeClue && (
          <button className="host-dismiss-btn" onClick={dismissClue}>
            ↩ Back to Board
          </button>
        )}
      </section>

      {/* Final Jeopardy wagering */}
      {round === 'final' && (
        <section className="host-section">
          <div className="host-section-label">FINAL JEOPARDY</div>

          {(!serverState?.fjPhase) && (
            <button className="host-fj-start-btn" onClick={fjStartWagering}>
              📝 Start Wagering Period
            </button>
          )}

          {(serverState?.fjPhase === 'wagering' || serverState?.fjPhase === 'question' || serverState?.fjPhase === 'thinking' || serverState?.fjPhase === 'review' || serverState?.fjPhase === 'answer') && (
            <div className="host-fj-wagers">
              <div className="host-fj-wagers-title">
                {serverState.fjPhase === 'wagering' ? 'Collecting Wagers'
                  : serverState.fjPhase === 'question' ? 'Reading Question'
                  : serverState.fjPhase === 'thinking' ? '⏱ Timer Running'
                  : serverState.fjPhase === 'review' ? `🎙 Reviewing: ${teams[serverState.fjReviewIndex]?.name ?? ''}`
                  : 'Answer Revealed'}
              </div>
              {teams.map((team, i) => {
                const serverWager = serverState?.fjWagers?.[i];
                const serverAnswer = serverState?.fjAnswers?.[i];
                const reviewResult = serverState?.fjReviewResults?.[i];
                // During review, hide each team's wager until that team is judged
                // (drama: the audience sees response first, then verdict, then wager).
                const wagerVisible =
                  serverState.fjPhase !== 'review' ||
                  (i < serverState.fjReviewIndex) ||
                  (i === serverState.fjReviewIndex && serverState.fjReviewShowWager);
                return (
                  <div key={i} className="host-fj-wager-row">
                    <span className="host-fj-wager-team" style={{ color: TEAM_COLORS[i] }}>
                      {team.name}
                    </span>
                    {serverState.fjPhase === 'wagering' ? (
                      <input
                        className="host-fj-wager-input"
                        type="number"
                        min="0"
                        placeholder={serverWager !== null && serverWager !== undefined ? `$${serverWager.toLocaleString()} (from phone)` : 'Enter wager'}
                        value={fjWagerOverrides[i]}
                        onChange={e => fjSetWager(i, e.target.value)}
                      />
                    ) : (
                      <span className="host-fj-wager-amount">
                        {wagerVisible
                          ? (serverWager !== null && serverWager !== undefined ? `$${serverWager.toLocaleString()}` : '—')
                          : '🔒'}
                      </span>
                    )}
                    {serverAnswer && (serverState.fjPhase === 'thinking' || serverState.fjPhase === 'review' || serverState.fjPhase === 'answer') && (
                      <span className="host-fj-team-answer" title="Captain's submitted response">
                        “{serverAnswer}”
                      </span>
                    )}
                    {reviewResult === 'correct' && <span className="host-fj-team-result host-fj-team-correct">✓</span>}
                    {reviewResult === 'wrong' && <span className="host-fj-team-result host-fj-team-wrong">✗</span>}
                    {serverWager !== null && serverWager !== undefined && serverState.fjPhase === 'wagering' && (
                      <span className="host-fj-wager-phone-badge">📱</span>
                    )}
                  </div>
                );
              })}
              {serverState.fjPhase === 'wagering' && (
                <button className="host-fj-reveal-btn" onClick={fjRevealQuestion}>
                  ▶ Reveal Question
                </button>
              )}
              {serverState.fjPhase === 'question' && (
                <button className="host-fj-reveal-btn" onClick={fjStartTimer}>
                  ⏱ Start 30-Second Timer
                </button>
              )}
              {serverState.fjPhase === 'thinking' && (
                <button className="host-fj-reveal-btn host-fj-reveal-answer-btn" onClick={fjStartReview}>
                  ▶ Start Review
                </button>
              )}
              {serverState.fjPhase === 'review' && (() => {
                const idx = serverState.fjReviewIndex;
                const team = teams[idx];
                const answer = serverState.fjAnswers?.[idx];
                const wager = serverState.fjWagers?.[idx];
                const judged = serverState.fjReviewShowWager;
                const isLast = idx >= teams.length - 1;
                return (
                  <div className="host-fj-review">
                    <div className="host-fj-review-team" style={{ color: TEAM_COLORS[idx] }}>
                      {team?.name}
                    </div>
                    <div className="host-fj-review-answer">
                      {answer ? `“${answer}”` : <em>(no answer submitted)</em>}
                    </div>
                    {!judged && (
                      <div className="host-fj-review-judge">
                        <button className="host-correct-btn" onClick={() => fjReviewJudge(true)}>
                          ✓ Correct
                        </button>
                        <button className="host-wrong-btn" onClick={() => fjReviewJudge(false)}>
                          ✗ Wrong
                        </button>
                      </div>
                    )}
                    {judged && (
                      <>
                        <div className="host-fj-review-wager">
                          Wager: ${(wager ?? 0).toLocaleString()}
                          {' '}
                          <span className={serverState.fjReviewResults?.[idx] === 'correct' ? 'host-fj-team-correct' : 'host-fj-team-wrong'}>
                            ({serverState.fjReviewResults?.[idx] === 'correct' ? `+$${(wager ?? 0).toLocaleString()}` : `-$${(wager ?? 0).toLocaleString()}`})
                          </span>
                        </div>
                        <button className="host-fj-reveal-btn" onClick={fjReviewNext}>
                          {isLast ? '👁 Reveal Final Answer' : '▶ Next Team'}
                        </button>
                      </>
                    )}
                  </div>
                );
              })()}
              {serverState.fjPhase === 'answer' && (
                <div className="host-fj-answered">✅ Answer revealed on game screen</div>
              )}
            </div>
          )}
        </section>
      )}

      {/* Board view */}
      <section className="host-section">
        <div className="host-section-label">BOARD</div>
        <div className="host-board-tabs">
          {(['single', 'double', 'final'] as const).map(r => (
            <button
              key={r}
              className={`host-board-tab ${boardViewRound === r ? 'host-board-tab-active' : ''}`}
              onClick={() => setBoardViewRound(r)}
            >
              {r === 'single' ? 'Single' : r === 'double' ? 'Double' : 'Final'}
            </button>
          ))}
        </div>

        {boardViewRound === 'final' ? (
          (() => {
            const final = (serverState?.gameData?.game?.final) ?? FALLBACK_GAME.final;
            return (
          <div className="host-board-final">
            <div className="host-board-final-cat">{final?.category}</div>
            <div className="host-board-final-clue">{final?.clue}</div>
            <div className="host-board-final-answer">{final?.solution}</div>
            <button className="host-board-final-btn" onClick={forceFinalClue}>
              Show Final Jeopardy
            </button>
          </div>
            );
          })()
        ) : (
          (() => {
            const currentGame = (serverState?.gameData?.game) ?? FALLBACK_GAME;
            return (
          <div className="host-board-grid">
            {/* Category headers */}
            <div className="host-board-row host-board-header-row">
              {currentGame[boardViewRound]?.map((cat: any, ci: number) => (
                <div key={ci} className="host-board-cat-header">{cat.category}</div>
              ))}
            </div>
            {/* Clue rows */}
            {currentGame[boardViewRound]?.[0]?.clues.map((_: any, clueIdx: number) => (
              <div key={clueIdx} className="host-board-row">
                {currentGame[boardViewRound].map((cat: any, catIdx: number) => {
                  const clue = cat.clues[clueIdx];
                  const isChosen = serverState?.chosenClues?.some(
                    (c: any) => c.round === boardViewRound && c.categoryIndex === catIdx && c.clueIndex === clueIdx
                  );
                  const isActive =
                    serverState?.activeClueMeta?.roundName === boardViewRound &&
                    serverState?.activeClueMeta?.categoryIndex === catIdx &&
                    serverState?.activeClueMeta?.clueIndex === clueIdx;
                  return (
                    <button
                      key={catIdx}
                      className={`host-board-cell ${isChosen ? 'host-board-cell-chosen' : ''} ${isActive ? 'host-board-cell-active' : ''}`}
                      onClick={() => forceClue(boardViewRound, catIdx, clueIdx)}
                      title={`${clue?.clue}\n\n${clue?.solution}`}
                    >
                      ${clue?.value}
                      {clue?.dailyDouble && <span className="host-board-dd">★</span>}
                    </button>
                  );
                })}
              </div>
            ))}
          </div>
            );
          })()
        )}
      </section>

      {/* Teams */}
      <section className="host-section">
        <div className="host-section-label">TEAMS</div>
        <div className="host-teams">
          {teams.map((team, i) => {
            const roster = serverState?.rosters?.[i] ?? [];
            const captain = serverState?.captains?.[i] ?? null;
            return (
            <div key={i} className="host-team-card">
              <div className="host-team-name">
                {team.name}
                <span className="host-team-buzzer-count" title="Registered buzzers">
                  📱 {serverState?.buzzerCount?.[i] ?? 0}
                </span>
              </div>

              {/* Roster — captain marked with ★, other members get a "Make captain" button */}
              <div className="host-team-roster">
                {roster.length === 0 ? (
                  <div className="host-team-roster-empty">No buzzers connected</div>
                ) : (
                  roster.map(name => (
                    <div key={name} className="host-team-roster-row">
                      <span className={`host-team-roster-name ${name === captain ? 'host-team-roster-captain' : ''}`}>
                        {name === captain ? '★ ' : ''}{name}
                      </span>
                      {name !== captain && (
                        <button
                          className="host-team-roster-promote"
                          onClick={() => setCaptain(i, name)}
                          title="Make captain — they'll enter wagers for DD/Final"
                        >
                          Make captain
                        </button>
                      )}
                    </div>
                  ))
                )}
              </div>

              {editingTeam === i ? (
                <input
                  className="host-score-input"
                  type="number"
                  value={editValue}
                  onChange={e => setEditValue(e.target.value)}
                  onBlur={confirmEdit}
                  onKeyDown={handleEditKey}
                  autoFocus
                />
              ) : (
                <div
                  className="host-score"
                  onClick={() => startEdit(i)}
                  title="Click to edit score"
                >
                  {formatScore(team.score)}
                </div>
              )}

              <input
                className="host-amount-input"
                type="number"
                value={amounts[i]}
                onChange={e => {
                  const next = [...amounts];
                  next[i] = parseInt(e.target.value, 10) || 0;
                  setAmounts(next);
                }}
              />

              <div className="host-btns">
                <button className="host-btn host-btn-correct" onClick={() => adjust(i, 1)}>
                  ✓ +${(amounts[i] || 0).toLocaleString()}
                </button>
                <button className="host-btn host-btn-wrong" onClick={() => adjust(i, -1)}>
                  ✗ −${(amounts[i] || 0).toLocaleString()}
                </button>
              </div>

              {/* Buzz in on behalf of this team */}
              <button
                className="host-test-buzz-btn"
                onClick={() => testBuzz(i)}
                title="Buzz in for this team (if they don't have a phone)"
              >
                🔔 Buzz In
              </button>

              {/* QR code for this team's buzzer */}
              <div className="host-qr-wrap">
                <div className="host-qr-label">📱 {team.name} Buzzer</div>
                <div className="host-qr-code">
                  <QRCodeSVG
                    value={`${origin}/buzz/${i}`}
                    size={100}
                    bgColor="#ffffff"
                    fgColor="#000000"
                    level="M"
                  />
                </div>
                <div className="host-qr-url">{origin}/buzz/{i}</div>
              </div>
            </div>
            );
          })}
        </div>
      </section>

      {/* Sound effects — host triggers, broadcast to all clients */}
      <section className="host-section">
        <div className="host-section-label">SOUND EFFECTS</div>
        <div className="host-sfx-grid">
          {([
            ['intro', '🎺 Intro'],
            ['board-fill', '✨ Board Fill'],
            ['daily-double', '⭐ Daily Double'],
            ['right-answer', '✓ Correct'],
            ['times-up', '⏱ Time’s Up'],
            ['theme', '🎵 Think Theme'],
            ['outro', '🏁 Outro'],
          ] as const).map(([key, label]) => (
            <button
              key={key}
              className="host-sfx-btn"
              onClick={() => send({ type: 'PLAY_SFX', name: key })}
            >
              {label}
            </button>
          ))}
        </div>
      </section>

      {/* Round controls */}
      <section className="host-section">
        <div className="host-section-label">JUMP TO ROUND</div>
        <div className="host-round-btns">
          {(['single', 'double', 'final', 'done'] as const).map(r => (
            <button
              key={r}
              className={`host-round-btn ${round === r ? 'host-round-btn-active' : ''}`}
              onClick={() => jumpToRound(r)}
            >
              {r === 'single' ? 'Single' : r === 'double' ? 'Double' : r === 'final' ? 'Final' : 'Done'}
            </button>
          ))}
        </div>
      </section>

      <HostTriviaAdmin
        questions={serverState?.triviaQuestions ?? []}
        triviaCurrentId={serverState?.triviaMode?.currentQuestion?.id ?? null}
        controllerCount={serverState?.triviaMode?.controllerCount ?? 0}
        onAdd={triviaAdd}
        onEdit={triviaEdit}
        onDelete={triviaDelete}
      />

      {/* Admin */}
      <section className="host-section host-admin">
        {!serverState?.gameStarted && (
          <button
            className="host-start-btn"
            onClick={() => send({ type: 'GAME_STARTED' })}
          >
            ▶ Start Game
          </button>
        )}
        <div className="host-admin-row">
          <button
            className={`host-reset-btn ${resetConfirm ? 'confirming' : ''}`}
            onClick={newGame}
          >
            {resetConfirm ? 'Tap again to confirm' : '⟳ New Game'}
          </button>
          <button
            className="host-reset-btn"
            onClick={resetScores}
          >
            Reset Scores Only
          </button>
        </div>
      </section>

    </div>
  );
}
